/**
 * Web-only interaction and validation looks for the SingleSelect and
 * MultiSelect option cards. Native has no pointer hover and no error state
 * on these cards, so these never reach the shared catalog module.
 */

import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';

export const OPTION_CARD_HOVER_BACKGROUND = colors.background.primary;
export const OPTION_CARD_HOVER_BORDER = colors.stroke.input;
export const OPTION_INDICATOR_HOVER_BORDER = colors.stroke.primary;
// The shared unselected indicator border (`stroke.divider`) reads about 1.3:1
// on white; a control boundary needs 3:1, which `stroke.input` meets.
export const OPTION_INDICATOR_BORDER = colors.stroke.input;
export const OPTION_INVALID_BORDER = colors.button.critical;

/**
 * Gap between the option text and its indicator on web. Native keeps the
 * wider shared `OPTION_CONTENT_GAP`; the web column is narrow enough at phone
 * widths that the wider gap pushes long labels into extra lines.
 */
export const WEB_OPTION_CONTENT_GAP = spacing.lg;

export interface OptionCardWebState {
  selected: boolean;
  hovered: boolean;
  invalid: boolean;
}

export function webOptionCardBackground(base: string, { hovered }: OptionCardWebState): string {
  return hovered ? OPTION_CARD_HOVER_BACKGROUND : base;
}

/**
 * Error wins. Hover darkens only the neutral resting border, so a selected
 * card's highlight border stays put.
 */
export function webOptionCardBorder(
  base: string,
  { hovered, invalid }: OptionCardWebState,
): string {
  if (invalid) return OPTION_INVALID_BORDER;
  if (hovered && base === colors.stroke.divider) return OPTION_CARD_HOVER_BORDER;
  return base;
}

/**
 * Error marks every indicator, selected or not. Otherwise a selected indicator
 * keeps its own look and an empty one darkens on hover.
 */
export function webOptionIndicatorBorder(
  base: string,
  { selected, hovered, invalid }: OptionCardWebState,
): string {
  if (invalid) return OPTION_INVALID_BORDER;
  if (selected) return base;
  if (hovered) return OPTION_INDICATOR_HOVER_BORDER;
  return OPTION_INDICATOR_BORDER;
}
